import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import SubmitButton from "../submitButton/SubmitButton";
import { sendEmailService } from "../../services/email/services";
import { useNotifications } from "../../context/Notifications";
import { parseEmailHtml } from "../../utils/parse/parse";
import { useTranslator } from "../../context/translator";
import {
  useGetCompanyQuery,
  useGetTemplatesQuery,
  useGetUserByIdQuery,
} from "store/api/apiSlice";

function ColdAccountEmailCommunicationModal({
  customer,
  text,
  disabled = false,
}) {
  const { translate } = useTranslator();
  const { user } = useSelector((state) => state.user);
  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  //const { emailTemplates } = useSelector((state) => state.template);
  const { data: templates, isLoading: templatesLoading } = useGetTemplatesQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  const [emailTemplates, setEmailTemplates] = useState([]);
  const { addNotification } = useNotifications();
  const [modalOpen, setModalOpen] = useState(false);
  const [template, setTemplate] = useState(emailTemplates[0]);
  const [isLoading, setIsLoading] = useState(false);
  const [classCommunications] = useState({
    true: "flex w-2.5 h-2.5 bg-teal-500 rounded-full me-1.5 flex-shrink-0",
    false: "flex w-2.5 h-2.5 bg-red-500 rounded-full me-1.5 flex-shrink-0",
  });

  const toggleModal = () => {
    setTemplate(emailTemplates[0]);
    setModalOpen(!modalOpen);
  };

  const handleSendEmail = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    setIsLoading(true);

    const response = await sendEmailService({
      userId: userProfile?.user?._id,
      templateId: template?._id,
      customerId: customer?._id,
    });

    if (response?.sentEmail) {
      addNotification({
        id: Date.now(),
        message: translate("email-sent"),
        kind: "success",
      });

      setIsLoading(false);
      toggleModal();
    } else {
      addNotification({
        id: Date.now(),
        message: translate("error-sending-email"),
        kind: "danger",
      });
    }
    setIsLoading(false);
  };

  const onChangeTemplate = (event) => {
    const value = event.target.value;
    const temp = emailTemplates.find((temp) => temp._id === value);
    setTemplate(temp);
  };

  useEffect(() => {
    if (templates && templates?.templates?.length > 0) {
      setEmailTemplates(
        templates?.templates?.filter(
          (t) => t.type === "email" && !t.resetPassword && !t.newUser,
        ),
      );
    }
  }, [templates]);

  if (userLoading || companyLoading || templatesLoading) return null;

  return (
    <>
      <button
        onClick={toggleModal}
        className="flex items-center text-sm font-medium me-3 text-blue-600 dark:text-blue-500 hover:underline disabled:cursor-not-allowed disabled:text-gray-400"
        disabled={disabled}
      >
        <span
          className={classCommunications[customer.communications.email]}
        ></span>
        {text}: {customer.communications.email ? translate("yes") : "No"}
      </button>

      {modalOpen && (
        <div
          id="modalID"
          tabIndex="-1"
          aria-hidden="true"
          className="fixed top-0 right-0 left-0 z-50 flex justify-center items-center w-full h-full bg-black bg-opacity-50 overflow-y-auto"
        >
          <div className="relative p-4 w-full max-w-2xl max-h-full">
            <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
              <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {translate("email-of")}
                  {customer.fullName}
                </h3>
                <button
                  type="button"
                  className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                  data-modal-hide="modalID"
                  onClick={toggleModal}
                >
                  <svg
                    className="w-3 h-3"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 14"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                  <span className="sr-only">Close modal</span>
                </button>
              </div>
              <div className="p-4 md:p-5">
                <form method="POST" onSubmit={handleSendEmail}>
                  <div className="grid gap-6 mb-6 md:grid-cols-2">
                    <div>
                      <label
                        htmlFor="template"
                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                      >
                        {translate("select-template")}
                      </label>
                      <select
                        id="template"
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        defaultValue={emailTemplates[0]?._id}
                        onChange={onChangeTemplate}
                      >
                        {emailTemplates?.map((option) => (
                          <option key={option._id} value={option._id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <br />
                      <SubmitButton
                        isLoading={isLoading}
                        textLoading={translate("sending")}
                        text={translate("send")}
                      />
                    </div>
                  </div>
                  <div className="mb-6">
                    <span className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                      {translate("preview")}
                    </span>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: parseEmailHtml(
                          customer,
                          userProfile?.user,
                          company,
                          template.html,
                        ),
                      }}
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ColdAccountEmailCommunicationModal;
